import { Logger } from '@elliemae/pui-diagnostics';
import { removeDoubleSlash, escapeRegExp } from '../utils.js';

export const APP_STYLE_ID_PREFIX = 'ice-style-';

type LoadOptions = {
  name: string;
  hostUrl: string;
  documentEle: Document;
};

/**
 * A utility class for dynamically loading, managing, and removing stylesheet elements in the DOM.
 * @remarks
 * This class provides methods to:
 * - Add stylesheet link elements dynamically to the document
 * - Remove stylesheet elements by ID
 * - Clean up dynamically imported stylesheets by host URL
 * @example
 * ```typescript
 * const logger = new Logger();
 * const styleLoader = new StyleLoader(logger);
 *
 * // Add a stylesheet
 * await styleLoader.add({
 *   name: 'myApp',
 *   hostUrl: 'https://example.com',
 *   documentEle: document,
 *   fileName: 'styles.css',
 *   index: 0
 * });
 *
 * // Remove a stylesheet
 * await styleLoader.remove('ice-style-myapp-0', document);
 * ```
 */
export class StyleLoader {
  #logger: Logger;

  constructor(logger: Logger) {
    this.#logger = logger;
  }

  /**
   * Dynamically adds a stylesheet link element to the document.
   * @param options - Configuration options for the stylesheet
   * @param options.name - The name of the application or stylesheet
   * @param options.hostUrl - The base URL where the stylesheet is hosted
   * @param options.documentEle - The document object where the stylesheet should be added
   * @param options.fileName - The filename of the stylesheet to load
   * @param options.index - An index used to generate a unique stylesheet ID
   * @param options.id
   * @param options.href
   * @returns A promise that resolves with the link element's ID when the stylesheet loads successfully
   * @throws Will reject with an error if the stylesheet fails to load
   * @example
   * ```typescript
   * await styleLoader.add({
   *   name: 'myApp',
   *   hostUrl: 'https://cdn.example.com',
   *   documentEle: document,
   *   fileName: 'main.css',
   *   index: 0
   * });
   * ```
   */
  #loadStyle = ({
    id,
    href,
    documentEle,
  }: {
    id: string;
    href: string;
    documentEle: Document;
  }): Promise<string> =>
    new Promise((resolve, reject) => {
      const ele = documentEle.createElement('link');
      ele.id = id;
      ele.nonce = '__CSP_NONCE__';
      ele.rel = 'stylesheet';
      ele.href = removeDoubleSlash(href);
      ele.onload = resolve.bind(null, ele.id);
      ele.onerror = (err) => {
        this.#logger.error({
          message: `Error loading stylesheet ${ele.href}`,
          exception: new Error(err as string),
        });
        reject(
          new Error(`Unable to load stylesheet ${ele.href}`, {
            cause: new Error(err as string),
          }),
        );
      };
      documentEle.head.appendChild(ele);
    });

  /**
   * Loads multiple stylesheet assets in parallel.
   * @param assets - Array of stylesheet filenames (relative paths)
   * @param options - Configuration options for loading the stylesheets
   * @param options.name - The name of the application (used for generating stylesheet IDs)
   * @param options.hostUrl - The base URL for resolving stylesheet paths
   * @param options.documentEle - The document object where stylesheets should be added
   * @returns A promise that resolves with an array of stylesheet element IDs when all stylesheets are loaded
   * @remarks
   * This method loads all stylesheets in parallel using Promise.all for optimal performance.
   * Each stylesheet is assigned a unique ID based on the application name and its index.
   * Unlike scripts, stylesheets don't typically have execution order dependencies.
   * @example
   * ```typescript
   * const styleIds = await styleLoader.load(
   *   ['main.css', 'theme.css', 'components.css'],
   *   {
   *     name: 'myApp',
   *     hostUrl: 'https://example.com',
   *     documentEle: document
   *   }
   * );
   * ```
   */
  load = async (assets: string[], options: LoadOptions) => {
    const elementIds: string[] = [];
    const loadPromises = assets.map((fileName, index) => {
      const url = new URL(fileName, options.hostUrl);
      const id = `${APP_STYLE_ID_PREFIX}${options.name.toLowerCase()}-${index}`;
      elementIds.push(id);
      return this.#loadStyle({
        id,
        href: url.href,
        documentEle: options.documentEle,
      });
    });
    await Promise.all(loadPromises);
    return elementIds;
  };

  /**
   * Removes a stylesheet link element from the document by its ID.
   * @param elementId - The ID of the stylesheet element to remove (default: '')
   * @param documentEle - The document object from which to remove the stylesheet (default: document)
   * @returns A promise that resolves when the stylesheet element has been removed
   * @remarks
   * If the element with the specified ID is not found, a warning is logged but the promise still resolves.
   * @example
   * ```typescript
   * await styleLoader.remove('ice-style-myapp-0', document);
   * ```
   */
  remove = (elementId = '', documentEle = document): Promise<void> =>
    new Promise((resolve) => {
      const ele = documentEle.getElementById(elementId) as HTMLElement;
      if (!ele) {
        this.#logger.warn(`style with id ${elementId} not found`);
        resolve();
        return;
      }
      ele.remove();
      resolve();
    });

  /**
   * Removes all stylesheet link elements from the document that match a specific host URL pattern.
   * @param hostUrl - The host URL pattern to match against stylesheet hrefs
   * @param documentEle - The document object from which to remove stylesheets
   * @remarks
   * This method queries all elements with `rel="stylesheet"` and removes any link whose `href`
   * attribute matches the provided host URL pattern (case-insensitive). This is useful for
   * cleaning up dynamically imported stylesheets from a specific host.
   * The iteration happens in reverse order to safely remove elements while iterating.
   * @example
   * ```typescript
   * styleLoader.removeDynamicImportedStyles('https://cdn.example.com', document);
   * ```
   */
  removeDynamicImportedStyles = (
    hostUrl: string,
    documentEle: Document,
  ): void => {
    const hostPattern = new RegExp(escapeRegExp(hostUrl), 'i');
    const prefetchElements = documentEle.querySelectorAll('[rel="stylesheet"]');
    for (let index = prefetchElements.length - 1; index >= 0; index -= 1) {
      const ele = prefetchElements[index] as HTMLLinkElement;
      const { href } = ele;
      if (hostPattern.test(href)) ele.remove();
    }
  };
}
